import React, { useState, useEffect, useCallback } from 'react';
import OutfitAdminLayout from './OutfitAdminLayout';
import Step1Basics from './TrendWizardSteps/Step1Basics';
import Step2SelectCCI from './TrendWizardSteps/Step2SelectCCI';
import Step3FacetsCuration from './TrendWizardSteps/Step3FacetsCuration';
import Step4ReviewPublish from './TrendWizardSteps/Step4ReviewPublish';
import TrendLookFeedModal from './TrendLookFeedModal';
import { outfitAdminApi } from '../../../services/outfitAdminApi';

function TrendBuilder({ email, onLogout }) {
  // Mode: 'library' or 'wizard'
  const [viewMode, setViewMode] = useState('library');
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [genderFilter, setGenderFilter] = useState('all');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Wizard state (Steps 1 to 4)
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    gender: 'men',
    image: '',
  });
  const [selectedCCI, setSelectedCCI] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [curatedLookCount, setCuratedLookCount] = useState(0);

  // Look Feed Modal state
  const [feedModalOpen, setFeedModalOpen] = useState(false);
  const [selectedTrendForFeed, setSelectedTrendForFeed] = useState(null);

  const fetchTrendsList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchTrends(genderFilter);
      if (response && Array.isArray(response.data)) {
        setTrends(response.data);
      }
    } catch (err) {
      console.error('Failed to load trends:', err);
      setError(err.message || 'Failed to load trend collections');
    } finally {
      setLoading(false);
    }
  }, [genderFilter]);

  useEffect(() => {
    if (viewMode === 'library') {
      fetchTrendsList();
    }
  }, [viewMode, fetchTrendsList]);

  // Form data change handler
  const handleFormChange = (key, value) => {
    setFormData((prev) => {
      // If gender changes, reset subsequent selections
      if (key === 'gender' && prev.gender !== value) {
        setSelectedCCI(null);
        setSelectedFacets({});
      }
      return { ...prev, [key]: value };
    });
  };

  // Start new trend creation
  const handleStartWizard = () => {
    setFormData({
      name: '',
      gender: 'men',
      image: '',
    });
    setSelectedCCI(null);
    setSelectedFacets({});
    setCuratedLookCount(0);
    setWizardStep(1);
    setViewMode('wizard');
  };

  // Wizard publish success
  const handlePublishSuccess = (createdData) => {
    setSuccessMsg(
      `Trend '${createdData?.name || formData.name}' published successfully with ${
        createdData?.total_looks_count ?? curatedLookCount
      } curated looks.`
    );
    setViewMode('library');
    fetchTrendsList();
  };

  // Open Look Feed Modal
  const handleOpenFeed = (trend) => {
    setSelectedTrendForFeed(trend);
    setFeedModalOpen(true);
  };

  return (
    <OutfitAdminLayout
      email={email}
      onLogout={onLogout}
      pageTitle="Outfit Trend Builder & Curation"
    >
      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setError('')}>
            ✕
          </button>
        </div>
      )}

      {successMsg && (
        <div className="outfit-alert success">
          <span>{successMsg}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setSuccessMsg('')}>
            ✕
          </button>
        </div>
      )}

      {/* VIEW A: TRENDS LIBRARY */}
      {viewMode === 'library' && (
        <>
          <div className="outfit-card" style={{ marginBottom: '20px' }}>
            <div className="outfit-card-header" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <h2 className="outfit-card-title">Curated Trends Library</h2>
                <div className="kb-gender-pills">
                  <button
                    className={`kb-gender-pill ${genderFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setGenderFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`kb-gender-pill ${genderFilter === 'men' ? 'active' : ''}`}
                    onClick={() => setGenderFilter('men')}
                  >
                    Men
                  </button>
                  <button
                    className={`kb-gender-pill ${genderFilter === 'women' ? 'active' : ''}`}
                    onClick={() => setGenderFilter('women')}
                  >
                    Women
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="outfit-btn outfit-btn-primary"
                onClick={handleStartWizard}
              >
                + Create New Trend
              </button>
            </div>
          </div>

          {loading ? (
            <div className="outfit-loading-state">
              <div className="outfit-spinner" />
              <p>Loading curated trends library...</p>
            </div>
          ) : trends.length === 0 ? (
            <div className="outfit-empty-state">
              <p>No curated trend collections created yet.</p>
              <button
                type="button"
                className="outfit-btn outfit-btn-primary"
                style={{ marginTop: '16px' }}
                onClick={handleStartWizard}
              >
                Create First Trend
              </button>
            </div>
          ) : (
            <div className="trends-grid">
              {trends.map((trend) => (
                <div key={trend.outfit_id || trend._id} className="trend-card">
                  <div className="trend-card-image-wrap">
                    {trend.image ? (
                      <img
                        src={trend.image}
                        alt={trend.name}
                        className="trend-card-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="trend-card-image-placeholder">No Cover Image</div>
                    )}
                    <span className="trend-card-gender-pill">{trend.gender}</span>
                  </div>

                  <div className="trend-card-body">
                    <h3 className="trend-card-title">{trend.name}</h3>
                    <div className="trend-card-handle">{trend.handle}</div>

                    <div className="trend-card-stats">
                      <span className="trend-card-looks-count">
                        {trend.total_looks_count || 0} Curated Looks
                      </span>
                      {trend.created_at && (
                        <span className="trend-card-date">
                          {new Date(trend.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="trend-card-footer">
                    <button
                      type="button"
                      className="outfit-btn outfit-btn-secondary"
                      style={{ width: '100%' }}
                      onClick={() => handleOpenFeed(trend)}
                    >
                      View Looks Feed &rarr;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* VIEW B: 4-STEP TREND CREATION WIZARD */}
      {viewMode === 'wizard' && (
        <>
          {/* Stepper Header */}
          <div className="wizard-stepper">
            <div
              className={`wizard-step-item ${
                wizardStep === 1 ? 'active' : wizardStep > 1 ? 'completed' : ''
              }`}
            >
              <div className="wizard-step-circle">
                {wizardStep > 1 ? '✓' : '1'}
              </div>
              <span className="wizard-step-label">Basics & Gender</span>
            </div>

            <div
              className={`wizard-step-item ${
                wizardStep === 2 ? 'active' : wizardStep > 2 ? 'completed' : ''
              }`}
            >
              <div className="wizard-step-circle">
                {wizardStep > 2 ? '✓' : '2'}
              </div>
              <span className="wizard-step-label">Select CCI</span>
            </div>

            <div
              className={`wizard-step-item ${
                wizardStep === 3 ? 'active' : wizardStep > 3 ? 'completed' : ''
              }`}
            >
              <div className="wizard-step-circle">
                {wizardStep > 3 ? '✓' : '3'}
              </div>
              <span className="wizard-step-label">Interactive Facets</span>
            </div>

            <div
              className={`wizard-step-item ${
                wizardStep === 4 ? 'active' : ''
              }`}
            >
              <div className="wizard-step-circle">4</div>
              <span className="wizard-step-label">Review & Publish</span>
            </div>
          </div>

          {/* Stepper Content */}
          {wizardStep === 1 && (
            <Step1Basics
              formData={formData}
              onChange={handleFormChange}
              onNext={() => setWizardStep(2)}
            />
          )}

          {wizardStep === 2 && (
            <Step2SelectCCI
              gender={formData.gender}
              selectedCCI={selectedCCI}
              onSelectCCI={(cci) => setSelectedCCI(cci)}
              onNext={() => setWizardStep(3)}
              onBack={() => setWizardStep(1)}
            />
          )}

          {wizardStep === 3 && (
            <Step3FacetsCuration
              selectedCCI={selectedCCI}
              selectedFacets={selectedFacets}
              onChangeFacets={(facets, lookCount) => {
                setSelectedFacets(facets);
                setCuratedLookCount(lookCount);
              }}
              onNext={() => setWizardStep(4)}
              onBack={() => setWizardStep(2)}
            />
          )}

          {wizardStep === 4 && (
            <Step4ReviewPublish
              formData={formData}
              selectedCCI={selectedCCI}
              selectedFacets={selectedFacets}
              lookCount={curatedLookCount}
              onPublishSuccess={handlePublishSuccess}
              onBack={() => setWizardStep(3)}
            />
          )}

          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              type="button"
              className="outfit-btn outfit-btn-secondary"
              onClick={() => setViewMode('library')}
            >
              Cancel & Exit Wizard
            </button>
          </div>
        </>
      )}

      {/* VIEW C: TREND LOOK FEED MODAL */}
      <TrendLookFeedModal
        isOpen={feedModalOpen}
        onClose={() => {
          setFeedModalOpen(false);
          setSelectedTrendForFeed(null);
        }}
        trend={selectedTrendForFeed}
      />
    </OutfitAdminLayout>
  );
}

export default TrendBuilder;
